import Color from 'color';
import {
  useLocation,
  useParams,
} from 'react-router-dom';

export function getUrlData( host, location ) {
  const { pathname, search } = location;

  // isPublic means:
  // - we log in with generic YT creds
  // - we don't have access to anything related to a specific sales counselor
  // - we don't have any personalization
  let isPublic = false;

  // Do we show side nav?
  let hasChrome = true;

  // Platforms have related permissions in Drupal.
  let platform = 'yt';

  // Determine if YT is in a mode.
  const searchParams = new URLSearchParams(search);
  const mode = searchParams.get('mode');

  // Most pages have the format urlslug.viewyourtour.com/blah and will use the
  // normal navbar and whatnot. Public facting ones do not.
  //
  // Public facing urls don't have the subdomain and have this structure:
  // viewyourtour.com/:communityUrlSlug/:drupalEntityBundle/:sanitizedEntityTitle/:drupalNid.
  //
  // Since the PIB is the most common public facing page, we omit
  // :drupalEntityBundle.
  //
  // :sanitizedEntityTitle is never used for routing or fetching data, its just
  // there for humans.
  const hostArray = host.split( '.' );
  let urlSlug = hostArray.length > 1 ? hostArray[0] : null;

  // Considered a platform.
  // Pages have scroll and are given the same permissions as Pib.
  if ( urlSlug === 'www' ) {
    const pathParts = pathname.split( '/' );
    urlSlug = pathParts[1];
    isPublic = true;
    hasChrome = false;
    platform = 'page';
  }

  // Considered a platform.
  const isSiteSee = pathname.includes( 'map-embed' );
  if(isSiteSee) {
    isPublic = true;
    hasChrome = false;
    platform = 'siteSee';
  }

  // Considered a platform.
  const isKiosk = mode === 'kiosk';
  if ( isKiosk ) {
    isPublic = true;
    platform = 'kiosk';
  }

  const isMapTiles = pathname.includes( 'map-tiles' );
  if(isMapTiles) {
    isPublic = true;
    hasChrome = false;
  }

  return {
    platform,
    isPublic,
    urlSlug,
    mode,
    hasChrome,
  };
}

export function getEnvironmentData() {
  const apiUrl = process.env.REACT_APP_YOUR_TOUR_CMS_URL || 'https://clients.thevectre.com';
  return {
    apiUrl,
    jsonApiUrl: `${apiUrl}/jsonapi`,
  };
}

export function getSettings( host, location ) {
  const urlData = getUrlData( host, location );
  const envData = getEnvironmentData();
  const data = {
    ...urlData,
    ...envData,
  };
  return data;
}

/*
Process platform-specific feature flags into generic ones, taking into account
the current platform.
*/
export function processFeatureFlags( platform, isPublic, featureFlags ) {
  let allowLogin = false;
  let platformShowOccupancy = true;
  let platformShowOccupants = false;
  let platformShowPricing = false;
  let platformShowPricingInitially = false;
  let platformShowSoldOutStatus = false;
  let platformShowNonRichLabelInitially = false;
  let platformShowMapPlaceLabelsInitially = false;

  if ( platform && featureFlags?.length > 0 ) {
    allowLogin = !isPublic && featureFlags?.includes( 'admin_login' );
    switch (platform) {
      case 'yt' :
        platformShowOccupancy = featureFlags?.includes( 'yt_show_occupancy' );
        platformShowOccupants = featureFlags?.includes( 'yt_show_occupancy' ) && featureFlags?.includes( 'yt_show_occupants' );
        platformShowPricing = featureFlags?.includes( 'yt_show_pricing' );
        platformShowPricingInitially = featureFlags?.includes( 'yt_show_pricing_initially');
        platformShowSoldOutStatus = featureFlags?.includes( 'yt_show_sold_out_status' );
        platformShowNonRichLabelInitially = featureFlags?.includes( 'yt_show_lower_value_place_labels_initially' );
        platformShowMapPlaceLabelsInitially = featureFlags?.includes( 'yt_show_map_place_labels_initially' );
        break;
      case 'kiosk' :
        platformShowOccupancy = featureFlags?.includes( 'kiosk_show_occupancy' );
        platformShowOccupants = featureFlags?.includes( 'kiosk_show_occupancy' ) && featureFlags?.includes( 'kiosk_show_occupants' );
        platformShowPricing = featureFlags?.includes( 'kiosk_show_pricing' );
        platformShowPricingInitially = featureFlags?.includes( 'kiosk_show_pricing_initially');
        platformShowSoldOutStatus = featureFlags?.includes( 'kiosk_show_sold_out_status' );
        platformShowNonRichLabelInitially = featureFlags?.includes( 'kiosk_show_lower_value_place_labels_initially' );
        platformShowMapPlaceLabelsInitially = featureFlags?.includes( 'kiosk_show_map_place_labels_initially' );
        break;
      case 'siteSee' :
        platformShowOccupancy = featureFlags?.includes( 'sitesee_show_occupancy' );
        platformShowOccupants = featureFlags?.includes( 'sitesee_show_occupancy' ) && featureFlags?.includes( 'sitesee_show_occupants' );
        platformShowPricing = featureFlags?.includes( 'sitesee_show_pricing' );
        platformShowPricingInitially = featureFlags?.includes( 'sitesee_show_pricing_initially' );
        platformShowSoldOutStatus = featureFlags?.includes( 'sitesee_show_sold_out_status' );
        platformShowNonRichLabelInitially = featureFlags?.includes( 'sitesee_show_lower_value_place_labels_initially' );
        platformShowMapPlaceLabelsInitially = featureFlags?.includes( 'sitesee_show_map_place_labels_initially' );
        break;
      case 'page' :
        platformShowOccupancy = featureFlags?.includes( 'pib_show_occupancy' );
        platformShowPricing = featureFlags?.includes( 'pib_show_pricing' );
        platformShowSoldOutStatus = featureFlags?.includes( 'pib_show_sold_out_status' );
        break;
    }
  }

  return {
    allowLogin,
    platformShowOccupancy,
    platformShowOccupants,
    platformShowPricing,
    platformShowPricingInitially,
    platformShowSoldOutStatus,
    platformShowNonRichLabelInitially,
    platformShowMapPlaceLabelsInitially,
  }
}

export function makeColors( primaryColor, secondaryColor ) {
  const primaryColorFactory = Color( primaryColor );
  const secondaryColorFactory = Color( secondaryColor );
  const colors = {
    primaryColor,
    secondaryColor,
    primaryLight10: primaryColorFactory.fade( 0.9 ),
    primaryDarkened: primaryColorFactory.darken( 0.2 ),
    secondaryColorLightened: secondaryColorFactory.isDark
      ? secondaryColorFactory.lightness( 90 )
      : secondaryColorFactory.lighten( 0.61 ),
    secondaryDarkened: secondaryColorFactory.darken( 0.2 ),
    secondaryLight80: secondaryColorFactory.fade( 0.8 ),
  };

  return colors;
}

export function createContainerStyleMarkup( primaryColor, secondaryColor ) {
  const colors = makeColors( primaryColor, secondaryColor );
  return {
    '--primary': primaryColor,
    '--primary--light--10': colors.primaryLight10,
    '--primary--darkened': colors.primaryDarkened,
    '--secondary': secondaryColor,
    '--secondary--lightened': colors.secondaryColorLightened,
    '--secondary--light--80': colors.secondaryLight80,
    '--secondary--darkened': colors.secondaryDarkened,
  };
}

// Add css variables to window root so they can be used in Portals that
// exist outside of app root.
export function setDocumentStyleProperties( window, primaryColor, secondaryColor ) {
  const colors = makeColors( primaryColor, secondaryColor );
  window.document.documentElement.style.setProperty(
    '--primary',
    colors.primaryColor,
  );
  window.document.documentElement.style.setProperty(
    '--primary--light--10',
    colors.primaryLight10,
  );
  window.document.documentElement.style.setProperty(
    '--primary--darkened',
    colors.primaryDarkened,
  );
  window.document.documentElement.style.setProperty(
    '--secondary',
    colors.secondaryColor,
  );
  window.document.documentElement.style.setProperty(
    '--secondary--lightened',
    colors.secondaryColorLightened,
  );
  window.document.documentElement.style.setProperty(
    '--secondary--light--80',
    colors.secondaryLight80,
  );
  window.document.documentElement.style.setProperty(
    '--secondary--darkened',
    colors.secondaryDarkened,
  );
}

export function checkIfPageEnabled( settings, ytConfig ) {
  const {
    enableKioskMode,
    enableSiteSee,
  } = ytConfig;
  const { platform } = settings;

  let allowAccess = true;
  if ( platform === 'kiosk' && !enableKioskMode ) {
    allowAccess = false;
  } else if ( platform === 'siteSee' && !enableSiteSee ) {
    allowAccess = false;
  }

  return allowAccess;
}
